import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MessagingService } from '../../../services/messagingServices/messaging.service';
import { MessagingProviderService } from '../../../services/messagingProviderServices/messaging-provider.service';
import {
  TemplatePreviewDialogComponent,
  ITemplatePreviewDialogData
} from '../template-preview-dialog/template-preview-dialog.component';
import { AddAwsEmailTemplateComponent } from '../messaging-templates/add-aws-email-template/add-aws-email-template.component';
import { AddPinBotTemplateComponent } from '../messaging-templates/add-pinbot-template/add-pinbot-template.component';
import { IMessagingProviderCredentials, ISystemTemplate } from '../../../iModels/iCommonModels/messaging.model';

export type MessagingChannelType = 'Email' | 'WhatsApp' | 'SMS' | 'Notification';
const ADD_NEW = '__ADD_NEW__';

// TODO: temporary static PinBot credentials until per-property config is wired up
const STATIC_PINBOT_CREDENTIALS: IMessagingProviderCredentials = {
  WabaId: '3554327921403275',
  ApiKey: '34d87099-75fc-11f1-894a-02c8a5e042bd'
};

export interface ISelectedMessageTemplate {
  id: string | number;
  name: string;
  subject?: string;
  content: string;
  placeholderTokens?: string[];
  awsTemplateVariables?: string[];
  systemTemplateId?: number;
}

interface IOption {
  value: string | number;
  label: string;
}

/**
 * Reusable provider -> template -> preview selector, shared across every screen
 * that sends a customer message (SMS/Email/WhatsApp/Notification).
 */
@Component({
  selector: 'app-messaging-provider-template',
  templateUrl: './messaging-provider-template.component.html',
  styleUrls: ['./messaging-provider-template.component.scss']
})
export class MessagingProviderTemplateComponent implements OnInit {
  @Input() channelType: MessagingChannelType;
  @Input() disabled = false;
  @Output() providerChange = new EventEmitter<string | number>();
  @Output() templateSelected = new EventEmitter<ISelectedMessageTemplate>();

  providerOptions: IOption[] = [];
  templateOptions: IOption[] = [];
  selectedProvider: string | number = '';
  selectedTemplateId: string | number = '';
  loadingTemplates = false;
  showTemplatePicker = false;
  private selectedTemplateDetail: ISelectedMessageTemplate;
  private credentials: IMessagingProviderCredentials;
  private pinBotTemplates: any[] = [];

  constructor(
    private messagingService: MessagingService,
    private messagingProviderService: MessagingProviderService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadProviderOptions();
  }

  private loadProviderOptions(): void {
    if (this.channelType === 'Email') {
      this.providerOptions = [
        { value: 'GMAIL', label: 'Gmail' },
        { value: 'AWS', label: 'AWS SES' }
      ];
    } else if (this.channelType === 'WhatsApp') {
      this.providerOptions = [
        { value: 'PINBOT', label: 'PinBot (WhatsApp Business Templates)' },
        { value: 'WHATSAPP', label: 'Custom Gateway' }
      ];
    } else {
      this.messagingProviderService.getProviders(this.channelType as 'SMS' | 'Notification')
        .subscribe((providers) => {
          this.providerOptions = providers.map((p) => ({ value: p.id, label: p.name }));
        });
    }
  }

  onProviderChange(value: string | number): void {
    this.selectedProvider = value;
    this.selectedTemplateId = '';
    this.templateOptions = [];
    this.pinBotTemplates = [];
    this.selectedTemplateDetail = null;
    this.providerChange.emit(value);

    this.showTemplatePicker = this.channelType === 'Email' ? value === 'AWS'
      : this.channelType === 'WhatsApp' ? value === 'PINBOT'
        : true;

    if (!this.showTemplatePicker) { return; }

    this.loadingTemplates = true;
    if (this.channelType === 'Email') {
      this.loadAwsTemplates();
    } else if (this.channelType === 'WhatsApp') {
      this.credentials = STATIC_PINBOT_CREDENTIALS;
      this.loadPinBotTemplates();
    } else {
      this.messagingProviderService.getTemplates(this.channelType as 'SMS' | 'Notification', value as number)
        .subscribe((templates) => {
          this.templateOptions = templates.map((t) => ({ value: t.id, label: t.name }));
          this.loadingTemplates = false;
        }, () => this.loadingTemplates = false);
    }
  }

  private loadAwsTemplates(): void {
    this.messagingService.getAwsEmailTemplates().subscribe((res) => {
      this.templateOptions = [{ value: ADD_NEW, label: 'Add New +' },
        ...((res && res.Templates) || []).map((t) => ({ value: t.TemplateName, label: t.TemplateName }))];
      this.loadingTemplates = false;
    }, () => this.loadingTemplates = false);
  }

  private loadPinBotTemplates(): void {
    const creds = this.credentials || {};
    if (!creds.WabaId || !creds.ApiKey) {
      this.pinBotTemplates = [];
      this.templateOptions = [{ value: ADD_NEW, label: 'Add New +' }];
      this.loadingTemplates = false;
      return;
    }
    this.loadingTemplates = true;
    this.messagingService.getPinBotTemplates({ WabaId: creds.WabaId, ApiKey: creds.ApiKey })
      .pipe(
        switchMap((res) => {
          const templates: any[] = (res && res.data) || (res instanceof Array ? res : []);
          return this.messagingService.getSystemTemplates(creds.WabaId).pipe(
            catchError(() => of([] as ISystemTemplate[])),
            map((systemTemplates) => this.attachSystemTemplateIds(templates, systemTemplates))
          );
        })
      )
      .subscribe((templates) => {
        this.pinBotTemplates = templates;
        this.templateOptions = [{ value: ADD_NEW, label: 'Add New +' },
          ...this.pinBotTemplates.map((t) => ({ value: t.id || t.MessageTemplateId, label: t.name || t.Name }))];
        this.loadingTemplates = false;
      }, () => {
        this.pinBotTemplates = [];
        this.loadingTemplates = false;
      });
  }

  /** Adds `systemtemplateid` to each PinBot template by matching its name with the system template title. */
  private attachSystemTemplateIds(templates: any[], systemTemplates: ISystemTemplate[]): any[] {
    const idByTitle = new Map<string, number>();
    (Array.isArray(systemTemplates) ? systemTemplates : []).forEach((s) => idByTitle.set(s.temptitle, s.tempid));
    return templates.map((t) => ({ ...t, systemtemplateid: idByTitle.get(t.name || t.Name) }));
  }

  /** PinBot list response already carries the components, so the preview is built locally. */
  private buildPinBotDetail(templateId: string | number): ISelectedMessageTemplate {
    // tslint:disable-next-line:triple-equals
    const template = this.pinBotTemplates.find((t) => (t.id || t.MessageTemplateId) == templateId);
    if (!template) { return null; }
    const components: any[] = template.components || [];
    const body = components.find((c) => c.type === 'BODY') || {};
    const header = components.find((c) => c.type === 'HEADER') || {};
    const footer = components.find((c) => c.type === 'FOOTER') || {};
    const sample = ((body.example && body.example.body_text && body.example.body_text[0]) || []);
    const content = [header.text, this.fillPlaceholders(body.text, sample), footer.text]
      .filter((part) => !!part).join('\n\n');
    return {
      id: templateId,
      name: template.name || String(templateId),
      content,
      placeholderTokens: this.readPlaceholderTokens(body.text),
      systemTemplateId: template.systemtemplateid
    };
  }

  private fillPlaceholders(text: string, sample: string[]): string {
    if (!text) { return ''; }
    return text.replace(/{{\s*(\d+)\s*}}/g, (match, index) => {
      const value = sample[Number(index) - 1];
      return value === undefined ? match : value;
    });
  }

  private readPlaceholderTokens(text: string): string[] {
    return Array.from(new Set((text || '').match(/{{\s*\d+\s*}}/g) || []))
      .sort((left, right) => Number(left.replace(/\D/g, '')) - Number(right.replace(/\D/g, '')));
  }

  private readAwsTemplateVariables(text: string): string[] {
    const variables: string[] = [];
    const expression = /{{\s*([A-Za-z_][A-Za-z0-9_.-]*)\s*}}/g;
    let match: RegExpExecArray;
    while ((match = expression.exec(text || '')) !== null) {
      if (variables.indexOf(match[1]) === -1) {
        variables.push(match[1]);
      }
    }
    return variables;
  }

  onTemplateChange(templateId: string | number): void {
    if (templateId === ADD_NEW) {
      this.openAddTemplateDialog();
      return;
    }
    this.selectedTemplateId = templateId;
    if (!templateId) { this.selectedTemplateDetail = null; return; }

    if (this.channelType === 'Email' && this.selectedProvider === 'AWS') {
      this.messagingService.getAwsEmailTemplateDetails(templateId as string).subscribe((response: any) => {
        const detail = (response && (response.Template || response.template || response.data)) || response || {};
        const subject = detail.SubjectPart || detail.subjectPart;
        const html = detail.HtmlPart || detail.htmlPart;
        const text = detail.TextPart || detail.textPart;
        this.selectedTemplateDetail = {
          id: templateId,
          name: detail.TemplateName || detail.templateName || String(templateId),
          subject,
          content: html || text,
          awsTemplateVariables: this.readAwsTemplateVariables(
            [subject, html, text].filter((part) => !!part).join('\n')
          )
        };
        this.templateSelected.emit(this.selectedTemplateDetail);
      });
    } else if (this.channelType === 'WhatsApp' && this.selectedProvider === 'PINBOT') {
      this.selectedTemplateDetail = this.buildPinBotDetail(templateId);
      if (this.selectedTemplateDetail) {
        this.templateSelected.emit(this.selectedTemplateDetail);
        this.previewTemplate();
      }
    } else {
      const option = this.templateOptions.find((t) => t.value === templateId);
      this.messagingProviderService.getTemplates(this.channelType as 'SMS' | 'Notification', this.selectedProvider as number)
        .subscribe((templates) => {
          const match = templates.find((t) => t.id === templateId);
          this.selectedTemplateDetail = { id: templateId, name: option ? option.label : '', content: match ? match.content : '' };
          this.templateSelected.emit(this.selectedTemplateDetail);
        });
    }
  }

  previewTemplate(): void {
    if (!this.selectedTemplateDetail) { return; }
    const data: ITemplatePreviewDialogData = {
      title: this.selectedTemplateDetail.name,
      subject: this.selectedTemplateDetail.subject,
      content: this.selectedTemplateDetail.content,
      isHtml: this.channelType === 'Email' && this.selectedProvider === 'AWS'
    };
    this.dialog.open(TemplatePreviewDialogComponent, {
      width: '650px',
      panelClass: 'mat-dialog-box',
      data
    });
  }

  openAddTemplateDialog(existing?: ISelectedMessageTemplate): void {
    if (this.channelType === 'Email' && this.selectedProvider === 'AWS') {
      const dialogRef = this.dialog.open(AddAwsEmailTemplateComponent, {
        width: '600px',
        panelClass: 'mat-dialog-box',
        data: existing ? {
          TemplateName: existing.id, SubjectPart: existing.subject, HtmlPart: existing.content, isEdit: true
        } : {}
      });
      dialogRef.afterClosed().subscribe((result) => { if (result) { this.loadAwsTemplates(); } });
    } else if (this.channelType === 'WhatsApp' && this.selectedProvider === 'PINBOT') {
      const dialogRef = this.dialog.open(AddPinBotTemplateComponent, {
        width: '600px',
        panelClass: 'mat-dialog-box',
        data: {
          id: existing ? existing.id : undefined,
          name: existing ? existing.name : undefined,
          body: existing ? existing.content : undefined,
          isEdit: !!existing,
          credentials: this.credentials
        }
      });
      dialogRef.afterClosed().subscribe((result) => { if (result) { this.loadPinBotTemplates(); } });
    }
  }

  editSelectedTemplate(): void {
    if (this.selectedTemplateDetail) { this.openAddTemplateDialog(this.selectedTemplateDetail); }
  }

  deleteSelectedTemplate(): void {
    if (!this.selectedTemplateDetail) { return; }
    if (this.channelType === 'Email' && this.selectedProvider === 'AWS') {
      this.messagingService.deleteAwsEmailTemplate(this.selectedTemplateDetail.id as string).subscribe(() => {
        this.selectedTemplateId = '';
        this.selectedTemplateDetail = null;
        this.loadAwsTemplates();
      });
    } else if (this.channelType === 'WhatsApp' && this.selectedProvider === 'PINBOT') {
      const creds = this.credentials || {};
      this.messagingService.deletePinBotTemplate(
        this.selectedTemplateDetail.name, this.selectedTemplateDetail.id as string, creds.WabaId, creds.ApiKey
      ).subscribe(() => {
        this.selectedTemplateId = '';
        this.selectedTemplateDetail = null;
        this.loadPinBotTemplates();
      });
    }
  }
}
